"use client";

import {
  ChangeEvent,
  FormEvent,
  useMemo,
  useState,
} from "react";
import { supportedSubmissionSpecies } from "../workflow";
import { LanguageText } from "./LanguageText";

type SubmissionType = "new_study" | "correction";
type MetadataFormat = "none" | "csv" | "json";

type Receipt = {
  receiptId: string;
  trackingToken: string;
  status: string;
  createdAt: string;
};

type StatusResult = {
  receiptId: string;
  status: string;
  updatedAt: string;
  publicReviewNote?: string;
};

const metadataCharacterLimit = 65_536;
const privacyPolicyVersion = "operational-draft-2026-07-29";

const statusLabels: Record<string, { zh: string; en: string }> = {
  received: { zh: "已收到", en: "Received" },
  in_review: { zh: "审核中", en: "In review" },
  action_required: { zh: "需要提交者补充", en: "Action required" },
  approved: { zh: "已批准，等待发布", en: "Approved, awaiting release" },
  not_accepted: { zh: "未收录", en: "Not accepted" },
  published: { zh: "已发布", en: "Published" },
};

function responseMessage(value: unknown, fallback: string) {
  if (
    value &&
    typeof value === "object" &&
    "error" in value &&
    typeof value.error === "string"
  ) {
    return value.error;
  }
  return fallback;
}

export function AnonymousSubmissionForm({
  apiEndpoint = "/api/submissions",
  submissionPortalUrl,
}: {
  apiEndpoint?: string | null;
  submissionPortalUrl?: string | null;
}) {
  const [submissionType, setSubmissionType] =
    useState<SubmissionType>("new_study");
  const [metadataFormat, setMetadataFormat] =
    useState<MetadataFormat>("none");
  const [metadataText, setMetadataText] = useState("");
  const [metadataFileName, setMetadataFileName] = useState("");
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [statusResult, setStatusResult] = useState<StatusResult | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);

  const submissionEnabled = Boolean(apiEndpoint);
  const isCorrection = submissionType === "correction";
  const statusEndpoint = useMemo(
    () => (apiEndpoint ? `${apiEndpoint.replace(/\/$/, "")}/status` : null),
    [apiEndpoint],
  );

  async function readMetadataFile(event: ChangeEvent<HTMLInputElement>) {
    setError("");
    const file = event.currentTarget.files?.[0];
    if (!file) return;

    const lowerName = file.name.toLowerCase();
    const format: MetadataFormat = lowerName.endsWith(".csv")
      ? "csv"
      : lowerName.endsWith(".json")
        ? "json"
        : "none";

    if (format === "none") {
      setError("Only UTF-8 CSV or JSON metadata files are accepted · 仅接受 UTF-8 CSV 或 JSON 元数据文件。");
      event.currentTarget.value = "";
      return;
    }
    if (file.size > metadataCharacterLimit) {
      setError("The metadata file must be 64 KiB or smaller · 元数据文件不得超过 64 KiB。");
      event.currentTarget.value = "";
      return;
    }

    const text = await file.text();
    if (text.length > metadataCharacterLimit) {
      setError("The decoded metadata is too long · 解码后的元数据超过长度限制。");
      event.currentTarget.value = "";
      return;
    }
    if (format === "json") {
      try {
        JSON.parse(text);
      } catch {
        setError("The selected JSON file is not valid JSON · 所选文件不是有效 JSON。");
        event.currentTarget.value = "";
        return;
      }
    }
    if (format === "csv" && !text.trim().split(/\r?\n/, 1)[0]?.trim()) {
      setError("The CSV header is empty · CSV 表头为空。");
      event.currentTarget.value = "";
      return;
    }

    setMetadataFormat(format);
    setMetadataText(text);
    setMetadataFileName(file.name);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!apiEndpoint) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const text = (name: string) => String(data.get(name) ?? "").trim();
    if (metadataText.trim() && metadataFormat === "none") {
      setError(
        "Choose CSV or JSON for the metadata text · 请为元数据文本选择 CSV 或 JSON 格式。",
      );
      return;
    }
    if (
      isCorrection &&
      !["paperUrl", "repositoryUrl", "processedUrl", "codeUrl"].some((name) =>
        Boolean(text(name)),
      )
    ) {
      setError(
        "Provide at least one public source URL supporting the correction · 请至少提供一个支持更正的公开来源网址。",
      );
      return;
    }

    setBusy(true);
    setError("");
    setMessage("");
    setReceipt(null);

    const payload = {
      submissionType,
      submitterName: text("submitterName"),
      institution: text("institution"),
      email: text("email"),
      orcid: text("orcid"),
      title: text("title"),
      paperIdentifier: text("paperIdentifier"),
      paperUrl: text("paperUrl"),
      correctionTarget: isCorrection ? text("correctionTarget") : "",
      species: text("species"),
      strain: text("strain"),
      repositoryAccessions: text("repositoryAccessions"),
      repositoryUrl: text("repositoryUrl"),
      sequencingMethod: text("sequencingMethod"),
      referenceGenome: text("referenceGenome"),
      processedLayer: text("processedLayer"),
      processedUrl: text("processedUrl"),
      codeUrl: text("codeUrl"),
      licence: text("licence"),
      notes: text("notes"),
      metadataFormat: metadataText.trim() ? metadataFormat : "none",
      metadataText: metadataText.trim(),
      rightsConfirmed: data.get("rightsConfirmed") === "true",
      privacyConfirmed: data.get("privacyConfirmed") === "true",
      privacyPolicyVersion,
      website: text("website"),
    };

    try {
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = (await response.json()) as Partial<Receipt> & {
        error?: string;
      };
      if (!response.ok) {
        throw new Error(
          responseMessage(
            body,
            "The submission could not be received · 暂时无法接收提交。",
          ),
        );
      }
      if (!body.receiptId || !body.trackingToken || !body.status) {
        throw new Error(
          "The server did not return a complete receipt · 服务器未返回完整回执。",
        );
      }
      const nextReceipt: Receipt = {
        receiptId: body.receiptId,
        trackingToken: body.trackingToken,
        status: body.status,
        createdAt: body.createdAt || new Date().toISOString(),
      };
      setReceipt(nextReceipt);
      setStatusResult({
        receiptId: nextReceipt.receiptId,
        status: nextReceipt.status,
        updatedAt: nextReceipt.createdAt,
      });
      setMessage(
        "Submission received for schema and scientific review · 提交已进入格式与科学审核流程。",
      );
      form.reset();
      setSubmissionType("new_study");
      setMetadataFormat("none");
      setMetadataText("");
      setMetadataFileName("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  }

  async function checkStatus(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!statusEndpoint) return;
    setStatusBusy(true);
    setError("");
    setStatusResult(null);
    const data = new FormData(event.currentTarget);
    const receiptId = String(data.get("receiptId") ?? "").trim();
    const token = String(data.get("token") ?? "").trim();
    try {
      const response = await fetch(statusEndpoint, {
        method: "POST",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
        },
        body: JSON.stringify({ receiptId, trackingToken: token }),
        cache: "no-store",
      });
      const body = (await response.json()) as Partial<StatusResult> & {
        error?: string;
      };
      if (!response.ok) {
        throw new Error(
          responseMessage(
            body,
            "The receipt could not be verified · 无法核验该回执。",
          ),
        );
      }
      if (!body.receiptId || !body.status || !body.updatedAt) {
        throw new Error(
          "The server returned an incomplete status · 服务器返回的状态不完整。",
        );
      }
      setStatusResult(body as StatusResult);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setStatusBusy(false);
    }
  }

  function downloadReceipt() {
    if (!receipt) return;
    const blob = new Blob([`${JSON.stringify(receipt, null, 2)}\n`], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${receipt.receiptId}-trypcell-receipt.json`;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="public-submission-shell">
      {!submissionEnabled ? (
        <div className="submission-unavailable" role="status">
          <strong>
            <LanguageText
              zh="此静态镜像未连接提交服务"
              en="This static mirror is not connected to the submission service"
            />
          </strong>
          <LanguageText
            as="p"
            zh="图谱与数据仍可正常浏览，但此镜像不会假装已接收表单。请使用已连接的正式提交入口。"
            en="Atlas and data browsing still work, but this mirror will not pretend to receive a form. Use the connected submission portal."
          />
          {submissionPortalUrl ? (
            <a className="button button-dark" href={submissionPortalUrl}>
              <LanguageText zh="打开正式提交入口" en="Open submission portal" />
            </a>
          ) : null}
        </div>
      ) : null}

      {message ? (
        <p className="workflow-message success" aria-live="polite">
          {message}
        </p>
      ) : null}
      {error ? (
        <p className="workflow-message error" role="alert">
          {error}
        </p>
      ) : null}

      {receipt ? (
        <section className="submission-receipt" aria-labelledby="receipt-title">
          <div>
            <span>SUBMISSION RECEIPT · 提交回执</span>
            <h2 id="receipt-title">
              <LanguageText
                zh="请立即保存编号和查询令牌"
                en="Save the receipt ID and tracking token now"
              />
            </h2>
            <LanguageText
              as="p"
              zh="查询令牌只在本次提交成功后显示。它与回执编号一起用于查看状态，请勿公开分享。"
              en="The tracking token is shown only after this successful submission. Keep it private; it is required with the receipt ID to check status."
            />
          </div>
          <dl>
            <div>
              <dt>Receipt ID · 回执编号</dt>
              <dd><code>{receipt.receiptId}</code></dd>
            </div>
            <div>
              <dt>Tracking token · 查询令牌</dt>
              <dd><code>{receipt.trackingToken}</code></dd>
            </div>
            <div>
              <dt>Status · 状态</dt>
              <dd>{statusLabels[receipt.status]?.en || receipt.status}</dd>
            </div>
          </dl>
          <button className="button button-dark" type="button" onClick={downloadReceipt}>
            <LanguageText zh="下载回执 JSON" en="Download receipt JSON" />
          </button>
        </section>
      ) : null}

      <section className="submission-panel" aria-labelledby="submission-form-title">
        <header>
          <span>01 · PUBLIC SUBMISSION</span>
          <LanguageText
            as="h2"
            zh="提交新研究或更正现有记录"
            en="Submit a new study or correct an existing record"
          />
          <LanguageText
            as="p"
            zh="无需注册。大型矩阵和原始测序文件必须放在持久公共仓库中；本表只接收来源信息和不超过 64 KiB 的纯文本 CSV/JSON 元数据。提交不会自动公开。"
            en="No account is required. Large matrices and raw reads must remain in a persistent public repository; this form accepts source information and up to 64 KiB of plain-text CSV/JSON metadata. Nothing is published automatically."
          />
        </header>

        <form className="workflow-form anonymous-submission-form" onSubmit={submit}>
          <label>
            <LanguageText as="span" zh="提交类型" en="Submission type" />
            <select
              name="submissionType"
              value={submissionType}
              onChange={(event) =>
                setSubmissionType(event.target.value as SubmissionType)
              }
              required
            >
              <option value="new_study">New study · 新研究</option>
              <option value="correction">Correction · 更正</option>
            </select>
          </label>
          {isCorrection ? (
            <label>
              <LanguageText
                as="span"
                zh="要更正的 TrypCell 记录编号、网址或原提交回执编号"
                en="TrypCell record ID, URL or earlier receipt ID to correct"
              />
              <input name="correctionTarget" maxLength={320} required />
            </label>
          ) : null}
          <label className="form-wide">
            <LanguageText
              as="span"
              zh={isCorrection ? "更正摘要" : "研究标题"}
              en={isCorrection ? "Correction summary" : "Study title"}
            />
            <input name="title" maxLength={320} required />
          </label>

          <div className="submission-form-divider form-wide">
            <LanguageText zh="提交者与联系方式" en="Submitter and contact" />
          </div>
          <label>
            <LanguageText as="span" zh="姓名" en="Name" />
            <input name="submitterName" autoComplete="name" maxLength={160} required />
          </label>
          <label>
            <LanguageText as="span" zh="学校、实验室或机构" en="Institution or laboratory" />
            <input name="institution" autoComplete="organization" maxLength={240} required />
          </label>
          <label>
            <LanguageText as="span" zh="联系邮箱" en="Contact email" />
            <input name="email" type="email" autoComplete="email" maxLength={254} required />
          </label>
          <label>
            <LanguageText as="span" zh="ORCID（可选）" en="ORCID (optional)" />
            <input
              name="orcid"
              inputMode="url"
              maxLength={40}
              placeholder="https://orcid.org/…"
            />
          </label>

          <div className="submission-form-divider form-wide">
            <LanguageText zh="论文与公共来源" en="Paper and public sources" />
          </div>
          <label>
            <LanguageText
              as="span"
              zh={isCorrection ? "DOI、PMID 或其他支持来源编号（可选）" : "DOI、PMID 或预印本编号"}
              en={isCorrection ? "DOI, PMID or other source identifier (optional)" : "DOI, PMID or preprint identifier"}
            />
            <input name="paperIdentifier" maxLength={180} required={!isCorrection} />
          </label>
          <label>
            <LanguageText
              as="span"
              zh={isCorrection ? "支持更正的论文或预印本网址（四类来源至少填一项）" : "论文或预印本网址"}
              en={isCorrection ? "Supporting paper or preprint URL (provide at least one source URL)" : "Paper or preprint URL"}
            />
            <input name="paperUrl" type="url" maxLength={500} required={!isCorrection} />
          </label>
          <label className="form-wide">
            <LanguageText
              as="span"
              zh={isCorrection ? "公共仓库登录号（可选）" : "公共仓库登录号（可填写多个）"}
              en={isCorrection ? "Public repository accession(s) (optional)" : "Public repository accession(s)"}
            />
            <input name="repositoryAccessions" maxLength={500} required={!isCorrection} />
          </label>
          <label className="form-wide">
            <LanguageText
              as="span"
              zh={isCorrection ? "公共仓库网址（可选）" : "公共仓库网址"}
              en={isCorrection ? "Public repository URL (optional)" : "Public repository URL"}
            />
            <input name="repositoryUrl" type="url" maxLength={500} required={!isCorrection} />
          </label>

          <div className="submission-form-divider form-wide">
            <LanguageText zh="生物学与技术元数据" en="Biological and technical metadata" />
          </div>
          <label>
            <LanguageText as="span" zh="锥虫物种" en="Trypanosoma species" />
            <select name="species" required defaultValue="">
              <option value="" disabled>
                Select · 请选择
              </option>
              {supportedSubmissionSpecies.map((species) => (
                <option key={species} value={species}>
                  {species}
                </option>
              ))}
            </select>
          </label>
          <label>
            <LanguageText
              as="span"
              zh={isCorrection ? "菌株或分离株（可选）" : "菌株或分离株"}
              en={isCorrection ? "Strain or isolate (optional)" : "Strain or isolate"}
            />
            <input name="strain" maxLength={200} required={!isCorrection} />
          </label>
          <label>
            <LanguageText
              as="span"
              zh={isCorrection ? "单细胞测序方法（可选）" : "单细胞测序方法"}
              en={isCorrection ? "Single-cell sequencing method (optional)" : "Single-cell sequencing method"}
            />
            <input name="sequencingMethod" maxLength={240} required={!isCorrection} />
          </label>
          <label>
            <LanguageText
              as="span"
              zh={isCorrection ? "参考基因组及版本（可选）" : "参考基因组及版本"}
              en={isCorrection ? "Reference genome and version (optional)" : "Reference genome and version"}
            />
            <input name="referenceGenome" maxLength={240} required={!isCorrection} />
          </label>
          <label className="form-wide">
            <LanguageText
              as="span"
              zh={isCorrection ? "处理后表达层及单位（可选）" : "处理后表达层及单位"}
              en={isCorrection ? "Processed expression layer and unit (optional)" : "Processed expression layer and unit"}
            />
            <input name="processedLayer" maxLength={320} required={!isCorrection} />
          </label>
          <label>
            <LanguageText
              as="span"
              zh={isCorrection ? "支持更正的处理后数据网址（可选）" : "处理后数据网址（可选）"}
              en={isCorrection ? "Supporting processed-data URL (optional)" : "Processed-data URL (optional)"}
            />
            <input name="processedUrl" type="url" maxLength={500} />
          </label>
          <label>
            <LanguageText
              as="span"
              zh={isCorrection ? "支持更正的分析代码网址（可选）" : "分析代码网址（可选）"}
              en={isCorrection ? "Supporting analysis-code URL (optional)" : "Analysis-code URL (optional)"}
            />
            <input name="codeUrl" type="url" maxLength={500} />
          </label>
          <label className="form-wide">
            <LanguageText
              as="span"
              zh={isCorrection ? "数据许可或仓库条款（可选）" : "数据许可或仓库声明的使用条款"}
              en={isCorrection ? "Data licence or repository terms (optional)" : "Data licence or repository-stated terms"}
            />
            <input name="licence" maxLength={200} required={!isCorrection} />
          </label>

          <div className="submission-form-divider form-wide">
            <LanguageText zh="可选元数据清单" en="Optional metadata manifest" />
          </div>
          <div className="submission-template-actions form-wide">
            <a href="/templates/trypcell-submission-template.csv" download>
              <LanguageText zh="下载 CSV 空白模板 ↓" en="Download blank CSV template ↓" />
            </a>
            <a href="/schema/submission.schema.json" target="_blank" rel="noreferrer">
              <LanguageText zh="查看 JSON 字段规范 →" en="Open JSON field schema →" />
            </a>
          </div>
          <label>
            <LanguageText as="span" zh="元数据格式" en="Metadata format" />
            <select
              name="metadataFormat"
              value={metadataFormat}
              onChange={(event) =>
                setMetadataFormat(event.target.value as MetadataFormat)
              }
            >
              <option value="none">None · 无</option>
              <option value="csv">UTF-8 CSV</option>
              <option value="json">JSON</option>
            </select>
          </label>
          <label>
            <LanguageText
              as="span"
              zh="读取小型 CSV/JSON 文件（不会上传原始二进制文件）"
              en="Read a small CSV/JSON file (no original binary upload)"
            />
            <input
              type="file"
              accept=".csv,.json,text/csv,application/json"
              onChange={readMetadataFile}
            />
            {metadataFileName ? <small>{metadataFileName}</small> : null}
          </label>
          <label className="form-wide">
            <LanguageText
              as="span"
              zh="元数据文本（最多 65,536 字符）"
              en="Metadata text (maximum 65,536 characters)"
            />
            <textarea
              name="metadataText"
              maxLength={metadataCharacterLimit}
              value={metadataText}
              onChange={(event) => setMetadataText(event.target.value)}
            />
            <small>
              {metadataText.length.toLocaleString()} / {metadataCharacterLimit.toLocaleString()}
            </small>
          </label>
          <label className="form-wide">
            <LanguageText
              as="span"
              zh={isCorrection ? "纠错说明与建议修改内容" : "给审核员的补充说明（可选）"}
              en={isCorrection ? "Correction details and proposed change" : "Notes for reviewers (optional)"}
            />
            <textarea name="notes" maxLength={2400} required={isCorrection} />
          </label>

          <label className="form-wide submission-consent">
            <input name="rightsConfirmed" type="checkbox" value="true" required />
            <LanguageText
              as="span"
              zh="我确认有权提交这些来源信息；内容不含患者、宿主个人身份信息、密码或未获授权的受限数据。"
              en="I confirm that I may submit this source information and that it contains no patient or host personal identifiers, passwords or unauthorized restricted data."
            />
          </label>
          <label className="form-wide submission-consent">
            <input name="privacyConfirmed" type="checkbox" value="true" required />
            <span>
              <LanguageText
                zh="我已阅读隐私说明，并同意为科学审核和审计保存联系方式、来源信息、状态与审核历史。"
                en="I have read the privacy notice and consent to retention of contact details, sources, status and review history for scientific review and audit."
              />{" "}
              <a href="/privacy/" target="_blank" rel="noreferrer">
                <LanguageText zh="阅读隐私说明 ↗" en="Read privacy notice ↗" />
              </a>
            </span>
          </label>
          <label className="submission-honeypot" aria-hidden="true">
            Website
            <input name="website" tabIndex={-1} autoComplete="off" />
          </label>
          <button
            className="button button-dark"
            type="submit"
            disabled={busy || !submissionEnabled}
          >
            {busy ? (
              "Submitting… · 正在提交…"
            ) : (
              <LanguageText zh="提交审核" en="Submit for review" />
            )}
          </button>
        </form>
      </section>

      <section className="submission-panel" aria-labelledby="status-form-title">
        <header>
          <span>02 · PRIVATE RECEIPT LOOKUP</span>
          <LanguageText as="h2" zh="查询提交状态" en="Check submission status" />
          <LanguageText
            as="p"
            zh="同时输入回执编号和私密查询令牌。公开查询只返回最少状态信息，不公开联系人或未审核的科学内容。"
            en="Enter both the receipt ID and private tracking token. The public lookup returns only minimal status information, never contact details or unreviewed scientific content."
          />
        </header>
        <form
          className="status-lookup-form"
          key={receipt?.receiptId || "empty-status-lookup"}
          onSubmit={checkStatus}
        >
          <label>
            <LanguageText as="span" zh="回执编号" en="Receipt ID" />
            <input
              name="receiptId"
              defaultValue={receipt?.receiptId}
              autoComplete="off"
              required
            />
          </label>
          <label>
            <LanguageText as="span" zh="查询令牌" en="Tracking token" />
            <input
              name="token"
              type="password"
              defaultValue={receipt?.trackingToken}
              autoComplete="off"
              required
            />
          </label>
          <button
            className="button button-dark"
            type="submit"
            disabled={statusBusy || !submissionEnabled}
          >
            {statusBusy ? (
              "Checking… · 正在查询…"
            ) : (
              <LanguageText zh="查询状态" en="Check status" />
            )}
          </button>
        </form>
        {statusResult ? (
          <div className="submission-status-result" aria-live="polite">
            <span>{statusResult.receiptId}</span>
            <strong>
              <LanguageText
                zh={statusLabels[statusResult.status]?.zh || statusResult.status}
                en={statusLabels[statusResult.status]?.en || statusResult.status}
              />
            </strong>
            <small>
              Updated · 更新：{new Date(statusResult.updatedAt).toLocaleString()}
            </small>
            {statusResult.publicReviewNote ? (
              <p>{statusResult.publicReviewNote}</p>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}
